/**
 * LogDetailModal - Full log event detail view
 * 
 * Modal overlay showing complete log data with:
 * - Syntax-highlighted JSON
 * - Copy to clipboard
 * - Prev/Next navigation
 * - Keyboard shortcuts (ESC, arrows)
 * - Clickable source links (VS Code protocol)
 * - Collapsible sections
 * - View related logs
 */

import React, { useEffect, useCallback, useState } from 'react';
import { X, Copy, ChevronLeft, ChevronRight, Check, RefreshCw, ExternalLink, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { JsonTreeView } from './JsonTreeView';
import { StackTraceViewer } from './StackTraceViewer';
import { decodeSourceLocation } from '../../../utils/SourceMapDecoder';
import type { LogEvent, SourceLocation } from '../../../utils/WeBelieveLogger.types';

interface LogDetailModalProps {
  log: LogEvent | null;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  onFilterRelated?: (sessionId: string, operation?: string) => void;
}

const LEVEL_COLORS: Record<string, string> = {
  debug: 'text-gray-500',
  info: 'text-blue-500',
  warn: 'text-yellow-500',
  error: 'text-red-500',
  performance: 'text-green-500',
};

// JSON syntax highlighter component
function SyntaxHighlightedJSON({ data }: { data: unknown }) {
  const jsonString = JSON.stringify(data, null, 2);
  
  // Parse and highlight JSON
  const highlighted = jsonString.replace(
    /("(?:\\.|[^"\\])*")\s*:/g, // Match keys
    '<span class="text-purple-600 dark:text-purple-400">$1</span>:'
  ).replace(
    /:\s*("(?:\\.|[^"\\])*")/g, // Match string values
    ': <span class="text-green-600 dark:text-green-400">$1</span>'
  ).replace(
    /:\s*(\d+\.?\d*)/g, // Match numbers
    ': <span class="text-blue-600 dark:text-blue-400">$1</span>'
  ).replace(
    /:\s*(true|false)/g, // Match booleans
    ': <span class="text-orange-600 dark:text-orange-400">$1</span>'
  ).replace(
    /:\s*(null)/g, // Match null
    ': <span class="text-gray-500 dark:text-gray-500">$1</span>'
  );
  
  return (
    <pre 
      className="bg-gray-100 dark:bg-gray-900 rounded-lg p-4 overflow-auto text-sm font-mono text-gray-800 dark:text-gray-200"
      dangerouslySetInnerHTML={{ __html: highlighted }}
    />
  );
}

// Collapsible section component
function CollapsibleSection({ 
  title, 
  children, 
  defaultOpen = true 
}: { 
  title: string; 
  children: React.ReactNode; 
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
      >
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        {title}
      </button>
      {isOpen && children}
    </div>
  );
}

// Generate file path and VS Code link from source location
// Returns both the full path (for copying) and VS Code SSH remote URL
function generateSourcePaths(source: SourceLocation, projectBasePath: string = '/home/edgelexllc/projects/edgelexapp'): { fullPath: string; vscodeUrl: string } | null {
  if (!source.file) return null;
  
  let filePath = source.file;
  
  // Handle WASM sources (e.g., "wasm/src/lib.rs:640:27 [typesetter_wasm]")
  // Extract just the file path part, removing line:column and module info
  if (filePath.startsWith('wasm/') || filePath.startsWith('src/')) {
    // Remove line:column info if embedded in the path (e.g., "wasm/src/lib.rs:640:27")
    const pathOnly = filePath.split(':')[0];
    
    if (pathOnly.startsWith('wasm/')) {
      filePath = `${projectBasePath}/typesetter/${pathOnly}`;
    } else {
      filePath = `${projectBasePath}/webelieve-frontend/${pathOnly}`;
    }
  } else if (!filePath.startsWith('/')) {
    // Relative path, assume frontend
    const pathOnly = filePath.split(':')[0];
    filePath = `${projectBasePath}/webelieve-frontend/${pathOnly}`;
  }
  
  // Build full path with line:column for display/copy
  let fullPath = filePath;
  if (source.line) {
    fullPath += `:${source.line}`;
    if (source.column) {
      fullPath += `:${source.column}`;
    }
  }
  
  // Build VS Code URL using SSH remote protocol
  // Format: vscode://vscode-remote/ssh-remote+HOST/path:line:column
  let vscodeUrl = `vscode://vscode-remote/ssh-remote+edgelexllc${filePath}`;
  if (source.line) {
    vscodeUrl += `:${source.line}`;
    if (source.column) {
      vscodeUrl += `:${source.column}`;
    }
  }
  
  return { fullPath, vscodeUrl };
}

export function LogDetailModal({
  log,
  onClose,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  onFilterRelated,
}: LogDetailModalProps) {
  const [copied, setCopied] = React.useState(false);
  const [decodedSource, setDecodedSource] = useState<SourceLocation | null>(null);
  const [isDecoding, setIsDecoding] = useState(false);

  // Decode source map when log changes (client-side using source maps)
  useEffect(() => {
    if (!log?.source) {
      setDecodedSource(null);
      return;
    }

    // Check if any part needs decoding (primary file or stack frames)
    const hasMinifiedFile = log.source.file?.includes('index-') && log.source.file?.endsWith('.js');
    const hasMinifiedStack = log.source.stack?.some(f =>
      f.file?.includes('index-') && f.file?.endsWith('.js')
    );
    const hasThirdPartySource = log.source.file?.includes('react-dom') ||
      log.source.file?.includes('node_modules');

    if (!hasMinifiedFile && !hasMinifiedStack && !hasThirdPartySource) {
      // Already decoded or doesn't need decoding
      setDecodedSource(null);
      return;
    }

    const decodeSource = async () => {
      setIsDecoding(true);
      try {
        // Use client-side source map decoder
        const decoded = await decodeSourceLocation(log.source!);

        // Only set if decoding actually changed something
        if (decoded.file !== log.source!.file ||
            decoded.culpritIndex !== log.source!.culpritIndex) {
          setDecodedSource(decoded);
        } else {
          setDecodedSource(null);
        }
      } catch (error) {
        console.error('[LogDetail] Failed to decode source:', error);
        setDecodedSource(null);
      } finally {
        setIsDecoding(false);
      }
    };

    decodeSource();
  }, [log?.source]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && hasPrev && onPrev) {
        onPrev();
      } else if (e.key === 'ArrowRight' && hasNext && onNext) {
        onNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  const handleCopy = useCallback(() => {
    if (!log) return;
    navigator.clipboard.writeText(JSON.stringify(log, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [log]);

  // Click outside to close
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!log) return null;

  const formattedDate = new Date(log.timestamp).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <span className={`text-lg font-semibold ${LEVEL_COLORS[log.level] || 'text-gray-500'}`}>
              {log.level.toUpperCase()}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {log.component}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Navigation */}
            <button
              onClick={onPrev}
              disabled={!hasPrev}
              className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Previous (←)"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={onNext}
              disabled={!hasNext}
              className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Next (→)"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            
            {/* Copy */}
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-500" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy JSON
                </>
              )}
            </button>
            
            {/* Close */}
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              title="Close (ESC)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Message */}
          <div className="mb-6">
            <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Message
            </h3>
            <p className="text-gray-900 dark:text-gray-100 text-lg">
              {log.message}
            </p>
          </div>

          {/* Metadata Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                Timestamp
              </h3>
              <p className="text-gray-900 dark:text-gray-100 font-mono text-sm">
                {formattedDate}
              </p>
            </div>
            
            <div>
              <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                Session ID
              </h3>
              <p className="text-gray-900 dark:text-gray-100 font-mono text-sm truncate" title={log.session_id}>
                {log.session_id}
              </p>
            </div>

            {log.operation && (
              <div>
                <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                  Operation
                </h3>
                <p className="text-gray-900 dark:text-gray-100 text-sm">
                  {log.operation}
                </p>
              </div>
            )}

            {log.duration_ms !== undefined && log.duration_ms !== null && (
              <div>
                <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                  Duration
                </h3>
                <p className="text-gray-900 dark:text-gray-100 font-mono text-sm">
                  {log.duration_ms.toFixed(2)}ms
                </p>
              </div>
            )}

            {log.source?.file && (
              <div className="col-span-2">
                <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                  Source {isDecoding && <RefreshCw className="inline w-3 h-3 animate-spin ml-1" />}
                </h3>
                {decodedSource ? (
                  <div>
                    {(() => {
                      const paths = generateSourcePaths(decodedSource);
                      return paths ? (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-900 dark:text-gray-100 font-mono text-sm">
                            {decodedSource.file}
                            {decodedSource.line && `:${decodedSource.line}`}
                            {decodedSource.column && `:${decodedSource.column}`}
                            {decodedSource.function && (
                              <span className="text-lex-accent ml-2">
                                in {decodedSource.function}()
                              </span>
                            )}
                          </span>
                          <button
                            onClick={() => navigator.clipboard.writeText(paths.fullPath)}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            title="Copy full path"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                          <a
                            href={paths.vscodeUrl}
                            className="p-1 text-gray-400 hover:text-lex-accent transition-colors"
                            title="Open in VS Code (SSH Remote)"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </div>
                      ) : (
                        <p className="text-gray-900 dark:text-gray-100 font-mono text-sm">
                          {decodedSource.file}
                          {decodedSource.line && `:${decodedSource.line}`}
                          {decodedSource.column && `:${decodedSource.column}`}
                          {decodedSource.function && (
                            <span className="text-lex-accent ml-2">
                              in {decodedSource.function}()
                            </span>
                          )}
                        </p>
                      );
                    })()}
                    <p className="text-gray-400 dark:text-gray-500 font-mono text-xs mt-1">
                      (minified: {log.source.file}:{log.source.line})
                    </p>
                  </div>
                ) : (
                  (() => {
                    const paths = generateSourcePaths(log.source!);
                    return paths ? (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-900 dark:text-gray-100 font-mono text-sm">
                          {log.source!.file}
                          {log.source!.line && `:${log.source!.line}`}
                          {log.source!.column && `:${log.source!.column}`}
                          {log.source!.function && (
                            <span className="text-gray-500 dark:text-gray-400 ml-2">
                              in {log.source!.function}()
                            </span>
                          )}
                        </span>
                        <button
                          onClick={() => navigator.clipboard.writeText(paths.fullPath)}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                          title="Copy full path"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <a
                          href={paths.vscodeUrl}
                          className="p-1 text-gray-400 hover:text-lex-accent transition-colors"
                          title="Open in VS Code (SSH Remote)"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    ) : (
                      <p className="text-gray-900 dark:text-gray-100 font-mono text-sm">
                        {log.source!.file}
                        {log.source!.line && `:${log.source!.line}`}
                        {log.source!.column && `:${log.source!.column}`}
                        {log.source!.function && (
                          <span className="text-gray-500 dark:text-gray-400 ml-2">
                            in {log.source!.function}()
                          </span>
                        )}
                      </p>
                    );
                  })()
                )}
              </div>
            )}

            {log.user_id && (
              <div>
                <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                  User ID
                </h3>
                <p className="text-gray-900 dark:text-gray-100 font-mono text-sm truncate" title={log.user_id}>
                  {log.user_id}
                </p>
              </div>
            )}

            {log.document_id && (
              <div>
                <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                  Document ID
                </h3>
                <p className="text-gray-900 dark:text-gray-100 font-mono text-sm truncate" title={log.document_id}>
                  {log.document_id}
                </p>
              </div>
            )}
          </div>

          {/* View Related Logs Button */}
          {onFilterRelated && (
            <div className="mb-6">
              <button
                onClick={() => {
                  onFilterRelated(log.session_id, log.operation);
                  onClose();
                }}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-lex-accent-soft text-lex-accent rounded-md hover:bg-lex-state-hover transition-colors"
              >
                <Filter className="w-4 h-4" />
                View Related Logs
                <span className="text-xs text-lex-accent">
                  (same session{log.operation ? ' + operation' : ''})
                </span>
              </button>
            </div>
          )}

          {/* Stack Trace - Premium visualization when full stack is available */}
          {/* Use decoded source if available, otherwise use original */}
          {((decodedSource?.stack && decodedSource.stack.length > 0) ||
            (log.source?.stack && log.source.stack.length > 0)) && (
            <div className="mb-6">
              <StackTraceViewer
                source={decodedSource || log.source!}
                defaultExpanded={true}
                showFrameNumbers={true}
                enableSearch={true}
              />
              {isDecoding && (
                <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Decoding source maps...
                </div>
              )}
            </div>
          )}

          {/* Data JSON - Collapsible with Interactive Tree View */}
          {log.data && Object.keys(log.data).length > 0 && (
            <CollapsibleSection title="Data" defaultOpen={true}>
              <JsonTreeView data={log.data} initialExpandDepth={2} />
            </CollapsibleSection>
          )}

          {/* Browser Info - Collapsible, default collapsed */}
          {log.browser_info && Object.keys(log.browser_info).length > 0 && (
            <CollapsibleSection title="Browser Info" defaultOpen={false}>
              <JsonTreeView data={log.browser_info} initialExpandDepth={1} />
            </CollapsibleSection>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 flex justify-between">
          <span>
            App: {log.app_version || 'N/A'} | WASM: {log.wasm_version || 'N/A'}
          </span>
          <span>
            Press ESC to close, ← → to navigate
          </span>
        </div>
      </div>
    </div>
  );
}
